// Floating right-click menu. Anchored at a screen-space (x, y); flips left
// or up if it would clip the viewport. Closes on outside click, Esc, or
// after an item runs.
//
// Items are passed as a list with optional separators. Each item is a plain
// object so the caller assembles the menu inline based on context (shape vs
// empty canvas, selection size, etc.).

import { useEffect, useRef } from 'react';

export interface MenuAction {
  label: string;
  shortcut?: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

export type MenuEntry = MenuAction | 'separator';

interface Props {
  x: number;
  y: number;
  entries: MenuEntry[];
  onDismiss: () => void;
}

const MIN_W = 200;
const ITEM_H = 28;
const SEP_H = 9;
const PAD_Y = 6;

export function ContextMenu({ x, y, entries, onDismiss }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      }
    };
    const onDown = (e: MouseEvent) => {
      const el = ref.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) onDismiss();
    };
    window.addEventListener('keydown', onKey);
    // Use mousedown (not click) so the menu closes before the click reaches
    // canvas — otherwise a click outside would also start a lasso.
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [onDismiss]);

  // Flip the menu against the viewport edges so it never opens off-screen.
  const estimatedH = entries.reduce(
    (acc, e) => acc + (e === 'separator' ? SEP_H : ITEM_H),
    PAD_Y * 2,
  );
  const left = x + MIN_W > window.innerWidth ? Math.max(8, x - MIN_W) : x;
  const top = y + estimatedH > window.innerHeight ? Math.max(8, y - estimatedH) : y;

  return (
    <div
      ref={ref}
      role="menu"
      style={{ left, top, minWidth: MIN_W }}
      className="
        fixed z-[10]
        bg-bg-panel border border-border-default rounded-2
        shadow-lg
        py-1.5
        text-13 text-fg-primary
        select-none
      "
    >
      {entries.map((entry, i) =>
        entry === 'separator' ? (
          <div
            key={`sep-${i}`}
            role="separator"
            className="h-px bg-border-subtle my-1 mx-1"
          />
        ) : (
          <MenuItem key={`${entry.label}-${i}`} action={entry} onDismiss={onDismiss} />
        ),
      )}
    </div>
  );
}

function MenuItem({ action, onDismiss }: { action: MenuAction; onDismiss: () => void }) {
  const handle = () => {
    if (action.disabled) return;
    action.onClick();
    onDismiss();
  };
  return (
    <button
      type="button"
      role="menuitem"
      onClick={handle}
      disabled={action.disabled}
      className={[
        'w-full h-7 px-3',
        'inline-flex items-center justify-between gap-6',
        'text-left',
        'focus-visible:outline-none',
        action.disabled
          ? 'opacity-40 cursor-not-allowed'
          : action.destructive
            ? 'hover:bg-error-subtle hover:text-error focus-visible:bg-error-subtle focus-visible:text-error'
            : 'hover:bg-bg-hover focus-visible:bg-bg-hover',
      ].join(' ')}
    >
      <span>{action.label}</span>
      {action.shortcut && (
        <span className="font-mono text-12 text-fg-tertiary tabular-nums">
          {action.shortcut}
        </span>
      )}
    </button>
  );
}
